/**
 * GeometricDecor – újrahasználható geometrikus dekorációs elemek
 * A logó vonalazásából és arányaiból inspirálva:
 * - Koncentrikus körök (aránymetszet-utalás)
 * - Finom rácsvonalak
 * - Szögelt vonalak (az "iV" betű szögei)
 * - Kereszt/tengely jelölők
 */

interface GoldenCirclesProps {
  size?: number;
  opacity?: number;
  color?: string;
  className?: string;
  animated?: boolean;
}

/** Koncentrikus körök – aránymetszet arányokban (1 : 1.618 : 2.618) */
export function GoldenCircles({
  size = 320,
  opacity = 0.08,
  color = "#acd1c6",
  className = "",
  animated = false,
}: GoldenCirclesProps) {
  const r1 = size / 2;
  const r2 = r1 / 1.618;
  const r3 = r2 / 1.618;
  const r4 = r3 / 1.618;

  return (
    <div
      className={`pointer-events-none absolute ${className}`}
      style={{ width: size, height: size, opacity }}
    >
      {/* Outer ring */}
      <div
        className={`absolute inset-0 rounded-full border ${animated ? "animate-[spin_60s_linear_infinite]" : ""}`}
        style={{ borderColor: color, borderWidth: "1px" }}
      />
      {/* Second ring */}
      <div
        className="absolute rounded-full border"
        style={{
          borderColor: color,
          borderWidth: "1px",
          top: `${r1 - r2}px`,
          left: `${r1 - r2}px`,
          width: r2 * 2,
          height: r2 * 2,
        }}
      />
      {/* Third ring */}
      <div
        className="absolute rounded-full border"
        style={{
          borderColor: color,
          borderWidth: "1px",
          top: `${r1 - r3}px`,
          left: `${r1 - r3}px`,
          width: r3 * 2,
          height: r3 * 2,
        }}
      />
      {/* Inner dot ring */}
      <div
        className="absolute rounded-full border"
        style={{
          borderColor: color,
          borderWidth: "1px",
          top: `${r1 - r4}px`,
          left: `${r1 - r4}px`,
          width: r4 * 2,
          height: r4 * 2,
        }}
      />
      {/* Center dot */}
      <div
        className="absolute rounded-full"
        style={{
          backgroundColor: color,
          width: 4,
          height: 4,
          top: r1 - 2,
          left: r1 - 2,
        }}
      />
      {/* Cross axes */}
      <div
        className="absolute"
        style={{
          width: "100%",
          height: "1px",
          backgroundColor: color,
          top: r1,
          left: 0,
          opacity: 0.4,
        }}
      />
      <div
        className="absolute"
        style={{
          width: "1px",
          height: "100%",
          backgroundColor: color,
          top: 0,
          left: r1,
          opacity: 0.4,
        }}
      />
    </div>
  );
}

interface CornerMarkProps {
  position?: "tl" | "tr" | "bl" | "br";
  size?: number;
  color?: string;
  opacity?: number;
  className?: string;
}

/** Sarokjelölők – mérnöki rajz stílusban */
export function CornerMark({
  position = "tl",
  size = 24,
  color = "#acd1c6",
  opacity = 0.35,
  className = "",
}: CornerMarkProps) {
  const isTop = position === "tl" || position === "tr";
  const isLeft = position === "tl" || position === "bl";

  return (
    <div
      className={`pointer-events-none absolute ${className}`}
      style={{ opacity }}
    >
      {/* Horizontal line */}
      <div
        className="absolute"
        style={{
          width: size,
          height: "1px",
          backgroundColor: color,
          top: isTop ? 0 : "auto",
          bottom: isTop ? "auto" : 0,
          left: isLeft ? 0 : "auto",
          right: isLeft ? "auto" : 0,
        }}
      />
      {/* Vertical line */}
      <div
        className="absolute"
        style={{
          width: "1px",
          height: size,
          backgroundColor: color,
          top: isTop ? 0 : "auto",
          bottom: isTop ? "auto" : 0,
          left: isLeft ? 0 : "auto",
          right: isLeft ? "auto" : 0,
        }}
      />
    </div>
  );
}

interface DiagonalLinesProps {
  count?: number;
  color?: string;
  opacity?: number;
  className?: string;
  angle?: number;
}

/** Átlós vonalak – az "iV" betű V szögéből inspirálva */
export function DiagonalLines({
  count = 5,
  color = "#acd1c6",
  opacity = 0.06,
  className = "",
  angle = 45,
}: DiagonalLinesProps) {
  return (
    <div className={`pointer-events-none absolute overflow-hidden ${className}`} style={{ opacity }}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            width: "200%",
            height: "1px",
            backgroundColor: color,
            top: `${(i / (count - 1)) * 100}%`,
            left: "-50%",
            transform: `rotate(${angle}deg)`,
            transformOrigin: "center",
          }}
        />
      ))}
    </div>
  );
}

interface GridDotsProps {
  cols?: number;
  rows?: number;
  gap?: number;
  color?: string;
  opacity?: number;
  className?: string;
}

/** Pontmátrix – mérnöki rajz referencia pontok */
export function GridDots({
  cols = 8,
  rows = 6,
  gap = 28,
  color = "#acd1c6",
  opacity = 0.12,
  className = "",
}: GridDotsProps) {
  return (
    <div
      className={`pointer-events-none absolute ${className}`}
      style={{
        opacity,
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, ${gap}px)`,
        gridTemplateRows: `repeat(${rows}, ${gap}px)`,
        gap: 0,
      }}
    >
      {Array.from({ length: cols * rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-center justify-center"
          style={{ width: gap, height: gap }}
        >
          <div
            className="rounded-full"
            style={{ width: 2, height: 2, backgroundColor: color }}
          />
        </div>
      ))}
    </div>
  );
}

interface VShapeProps {
  width?: number;
  height?: number;
  color?: string;
  opacity?: number;
  className?: string;
  strokeWidth?: number;
}

/** V-alak – a logó "V" betűjéből inspirálva */
export function VShape({
  width = 120,
  height = 80,
  color = "#acd1c6",
  opacity = 0.12,
  className = "",
  strokeWidth = 1,
}: VShapeProps) {
  return (
    <div
      className={`pointer-events-none absolute ${className}`}
      style={{ width, height, opacity }}
    >
      {/* Left arm of V */}
      <div
        className="absolute"
        style={{
          width: `${Math.sqrt((width / 2) ** 2 + height ** 2)}px`,
          height: `${strokeWidth}px`,
          backgroundColor: color,
          top: 0,
          left: 0,
          transformOrigin: "left center",
          transform: `rotate(${Math.atan2(height, width / 2) * (180 / Math.PI)}deg)`,
        }}
      />
      {/* Right arm of V */}
      <div
        className="absolute"
        style={{
          width: `${Math.sqrt((width / 2) ** 2 + height ** 2)}px`,
          height: `${strokeWidth}px`,
          backgroundColor: color,
          top: 0,
          right: 0,
          transformOrigin: "right center",
          transform: `rotate(${-Math.atan2(height, width / 2) * (180 / Math.PI)}deg)`,
        }}
      />
    </div>
  );
}
